import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '../ui/Button';

// Calendly link
const CALENDLY_URL = "https://calendly.com/ciphergenix/30min";

const plans = [
  {
    name: "Startup Plan",
    price: "$2,500",
    period: "/month",
    description: "For digital health startups, small clinics, and early-stage biotech.",
    features: [
      "Essential AI security monitoring (up to 5 models)",
      "Basic threat detection",
      "HIPAA compliance",
      "Email support",
      "Standard integrations"
    ],
    cta: "Get Started",
    featured: false
  },
  {
    name: "Growth Plan",
    price: "$8,000",
    period: "/month",
    description: "For mid-sized healthcare organizations and growing biotech.",
    features: [
      "Advanced threat detection (up to 25 models)",
      "HIPAA, HITECH, FDA compliance reporting",
      "Priority support",
      "EMR/EHR integration",
      "Real-time monitoring dashboard",
      "Audit-ready reports"
    ],
    cta: "Get Started",
    featured: true
  },
  {
    name: "Enterprise Plan",
    price: "$150K+",
    period: "/year",
    description: "For major health systems, Fortune 500 pharma, and large biotech.",
    features: [
      "Custom security solutions",
      "Unlimited models",
      "Dedicated support team",
      "On-site deployment option",
      "Advanced compliance & SLA guarantees",
      "Custom integrations"
    ],
    cta: "Contact Sales",
    featured: false
  }
];

export default function PricingSection() {
  return (
    <section id="pricing" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <p className="text-[#1e3a5f] text-sm font-medium tracking-wider uppercase mb-4">Pricing</p>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-6">
            Scalable Solutions for Every<br />Healthcare Organization
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            From digital clinics to Fortune 500 health systems. 15-20% below established players while offering more value.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative p-8 rounded-3xl ${
                plan.featured 
                  ? 'bg-[#0a1628] text-white border-2 border-[#2dd4bf]' 
                  : 'bg-white border border-slate-200'
              }`}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 rounded-full bg-[#2dd4bf] text-[#0a1628] text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="mb-8">
                <h3 className={`text-xl font-medium mb-2 ${plan.featured ? 'text-white' : 'text-slate-900'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm mb-4 ${plan.featured ? 'text-slate-400' : 'text-slate-600'}`}>
                  {plan.description}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-semibold ${plan.featured ? 'text-white' : 'text-slate-900'}`}>
                    {plan.price}
                  </span>
                  <span className={plan.featured ? 'text-slate-400' : 'text-slate-500'}>
                    {plan.period}
                  </span>
                </div>
              </div>
              
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      plan.featured ? 'bg-[#2dd4bf]/20' : 'bg-slate-100'
                    }`}>
                      <Check className={`w-3 h-3 ${plan.featured ? 'text-[#2dd4bf]' : 'text-[#1e3a5f]'}`} />
                    </div>
                    <span className={`text-sm ${plan.featured ? 'text-slate-300' : 'text-slate-600'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              
              {/* Button wrapped in <a> tag */}
              <a 
                href={CALENDLY_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full"
              >
                <Button 
                  className={`w-full py-6 rounded-full transition-all active:scale-95 ${
                    plan.featured 
                      ? 'bg-[#2dd4bf] text-[#0a1628] hover:bg-[#2dd4bf]/90' 
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {plan.cta}
                </Button>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}